import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import AddOnPhoto from "ui/AddOnPhoto"
import Counter from "ui/Counter"
import { numberToCurrency } from "helpers/string"
import { stepAddOnQuantity } from "components/bookings/helpers"
import styles from "./BookingForm.module.css"

// New Booking's add-ons (redesign, step 1): a row per enabled add-on with its unit price and a
// "−  N  +" counter. A new list rather than AddOnsSelector, whose "Add" button layout the old
// embedded widget, the calculator and Edit Booking still use. Quantities keep each add-on's
// per-order limits: + from 0 jumps to the minimum, − below it removes the add-on.
const AddOnsSection = ({ addOns, selections, onChange }) => {
  const { t } = useTranslation()
  const availableAddOns = addOns.filter((addOn) => addOn && addOn.enabled !== false)
  if (!availableAddOns.length) return null

  const handleQuantityChange = (addOn, quantity, nextQuantity) => {
    const steppedQuantity = stepAddOnQuantity(addOn, quantity, nextQuantity - quantity)
    const nextSelections = { ...selections }
    if (steppedQuantity <= 0) delete nextSelections[addOn.id]
    else nextSelections[addOn.id] = steppedQuantity
    onChange({ target: { name: "add_ons", value: nextSelections } })
  }

  return (
    <div className={styles.addOnSection}>
      <div className={styles.addOnTitle}>{t("booking.add_ons.title")}</div>
      <div>
        {availableAddOns.map((addOn) => {
          const quantity = Number(selections?.[addOn.id] ?? 0)
          const maxQuantity = addOn.max_per_order ?? Infinity
          return (
            <Flex key={addOn.id} align="center" gap="var(--spacing-3xl)" className={styles.addOnRow}>
              <Flex align="center" gap="var(--spacing-m)" className={styles.addOnName}>
                <AddOnPhoto src={addOn.photo_url} alt={addOn.title} size={32} iconSize={16} />
                <span className={styles.addOnTitleText}>{addOn.title}</span>
              </Flex>
              <span className={styles.addOnPrice}>{numberToCurrency(addOn.price)}</span>
              <Counter
                value={quantity}
                decreaseLabel={t("booking.add_ons.fewer", { title: addOn.title })}
                increaseLabel={t("booking.add_ons.more", { title: addOn.title })}
                isDecreaseDisabled={quantity <= 0}
                isIncreaseDisabled={quantity >= maxQuantity}
                onChange={(nextQuantity) => handleQuantityChange(addOn, quantity, nextQuantity)}
              />
            </Flex>
          )
        })}
      </div>
    </div>
  )
}

AddOnsSection.propTypes = {
  addOns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      photo_url: PropTypes.string,
      enabled: PropTypes.bool,
      min_per_order: PropTypes.number,
      max_per_order: PropTypes.number
    })
  ),
  selections: PropTypes.object,
  onChange: PropTypes.func.isRequired
}

AddOnsSection.defaultProps = {
  addOns: [],
  selections: {}
}

export default AddOnsSection
